import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAll } from '@/lib/paginate'
import { useHousehold } from '@/context/HouseholdContext'

export interface Tag {
  id: string
  household_id: string
  name: string
  description: string | null
  archived: boolean
  created_at: string
}

// Por padrão só as ativas (desabilitadas somem dos seletores).
export function useTags(includeArchived = false) {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['tags', householdId, includeArchived],
    queryFn: async () => {
      let q = supabase.from('tags').select('*').eq('household_id', householdId).order('name')
      if (!includeArchived) q = q.eq('archived', false)
      const { data, error } = await q
      if (error) throw error
      return data as Tag[]
    },
  })
}

export interface TagInput {
  name: string
  description: string | null
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['tags'] })
  void qc.invalidateQueries({ queryKey: ['transactions'] })
}

export function useSaveTag() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    // Ao renomear, propaga o novo nome no array `tags` das transações.
    mutationFn: async ({ id, values, oldName }: { id?: string; values: TagInput; oldName?: string }) => {
      if (id) {
        const { error } = await supabase.from('tags').update(values).eq('id', id)
        if (error) throw error
        if (oldName && oldName !== values.name) {
          await renameOnTransactions(householdId, oldName, values.name)
          await renameOnRecurringRules(householdId, oldName, values.name)
        }
      } else {
        const { error } = await supabase.from('tags').insert({ ...values, household_id: householdId })
        if (error) throw error
      }
    },
    onSuccess: () => invalidate(qc),
  })
}

export function useSetTagArchived() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase.from('tags').update({ archived }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidate(qc),
  })
}

// Excluir a tag também a remove do array `tags` das transações.
export function useDeleteTag() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await removeFromTransactions(householdId, name)
      await removeFromRecurringRules(householdId, name)
      const { error } = await supabase.from('tags').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidate(qc),
  })
}

// Quantas transações usam a tag.
export async function fetchTagTxCount(householdId: string, name: string): Promise<number> {
  const { count, error } = await supabase
    .from('transactions')
    .select('id', { count: 'exact', head: true })
    .eq('household_id', householdId)
    .contains('tags', [name])
  if (error) throw error
  return count ?? 0
}

async function tagged(householdId: string, name: string) {
  // Pagina TODAS as transações com a tag (o cap de 1000 do PostgREST deixaria
  // linhas de fora ao renomear/excluir).
  return fetchAll<{ id: string; tags: string[] }>((from, to) =>
    supabase
      .from('transactions')
      .select('id, tags')
      .eq('household_id', householdId)
      .contains('tags', [name])
      .range(from, to),
  )
}

async function renameOnTransactions(householdId: string, oldName: string, newName: string) {
  for (const t of await tagged(householdId, oldName)) {
    const tags = [...new Set(t.tags.map((x) => (x === oldName ? newName : x)))]
    const { error } = await supabase.from('transactions').update({ tags }).eq('id', t.id)
    if (error) throw error
  }
}

async function removeFromTransactions(householdId: string, name: string) {
  for (const t of await tagged(householdId, name)) {
    const tags = t.tags.filter((x) => x !== name)
    const { error } = await supabase.from('transactions').update({ tags }).eq('id', t.id)
    if (error) throw error
  }
}

// Regras recorrentes guardam as tags no `template` (jsonb), fora do array `tags[]`
// das transações — renomear/excluir a tag no catálogo precisa propagar aqui também,
// senão as PRÓXIMAS ocorrências geradas voltam a nascer com o nome antigo.
async function recurringRulesWithTemplateTags(householdId: string) {
  const { data, error } = await supabase
    .from('recurring_rules')
    .select('id, template')
    .eq('household_id', householdId)
  if (error) throw error
  return (data ?? []) as { id: string; template: { tags?: string[] } & Record<string, unknown> }[]
}

async function renameOnRecurringRules(householdId: string, oldName: string, newName: string) {
  for (const r of await recurringRulesWithTemplateTags(householdId)) {
    const current = r.template.tags ?? []
    if (!current.includes(oldName)) continue
    const tags = [...new Set(current.map((x) => (x === oldName ? newName : x)))]
    const { error } = await supabase
      .from('recurring_rules')
      .update({ template: { ...r.template, tags } })
      .eq('id', r.id)
    if (error) throw error
  }
}

async function removeFromRecurringRules(householdId: string, name: string) {
  for (const r of await recurringRulesWithTemplateTags(householdId)) {
    const current = r.template.tags ?? []
    if (!current.includes(name)) continue
    const tags = current.filter((x) => x !== name)
    const { error } = await supabase
      .from('recurring_rules')
      .update({ template: { ...r.template, tags } })
      .eq('id', r.id)
    if (error) throw error
  }
}
